import { useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { ColDef } from "@/components/admin/shared/AdminTable";
import { AdminTable } from "@/components/admin/shared/AdminTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { DeleteConfirmModal } from "@/components/admin/shared/DeleteConfirmModal";
import { PageHeader, AddButton, SaveButton, CancelButton, ViewButton, EditButton, DeleteButton } from "@/components/admin/shared/PageHeader";
import { TableSearch } from "@/components/admin/shared/TableSearch";
import { PennField, PennInput, PennDropdown, PennCheckbox } from "@/components/ui";
import type { ManualQuoteItemFormData } from "@/types/admin/manualQuoteItem.types";
import { manualQuoteItemSchema } from "@/types/admin/manualQuoteItem.types";
import {
  useCreateManualQuoteItem,
  useDeleteManualQuoteItem,
  useManualQuoteItems,
  useUpdateManualQuoteItem,
} from "@/hooks/useManualQuoteItems";
import type {
  CreateManualQuoteItemBody,
  ManualQuoteItem,
  UpdateManualQuoteItemBody,
} from "@/api/manualQuoteItems";

type ModalMode = "add" | "edit" | "view";
type ModalState = { mode: ModalMode; item?: ManualQuoteItem } | null;

const CURRENCY_OPTIONS = [
  { value: "EUR", label: "EUR" },
  { value: "USD", label: "USD" },
];

const newBlank = (): ManualQuoteItemFormData => ({
  id: crypto.randomUUID(), isManual: true,
  label: "", partNo: "", description: "", category: "",
  toolingType: "", machineType: "", itemName: "", partName: "",
  currency: "EUR", price: 0, qty: 1,
});

const apiErrorMessage = (err: unknown, fallback: string): string => {
  if (err && typeof err === "object" && "response" in err) {
    const data = (err as { response?: { data?: { error?: { message?: string } } } }).response?.data;
    if (data?.error?.message) return data.error.message;
  }
  return err instanceof Error ? err.message : fallback;
};

/** Map an API record into the form shape. */
const toForm = (i: ManualQuoteItem): ManualQuoteItemFormData => ({
  id: i.id,
  isManual: i.isManual ?? true,
  label: i.label ?? "",
  partNo: i.partNo ?? "",
  description: i.description ?? "",
  category: i.category ?? "",
  toolingType: i.toolingType ?? "",
  machineType: i.machineType ?? "",
  itemName: i.itemName ?? "",
  partName: i.partName ?? "",
  currency: (i.currency as "EUR" | "USD") ?? "EUR",
  price: i.price ? Number(i.price) : 0,
  qty: i.qty ?? 1,
});

/** Map form data into the API write body. Empty optional strings → null. */
const toBody = (form: ManualQuoteItemFormData): Omit<CreateManualQuoteItemBody, "description" | "currency" | "price" | "qty"> & {
  description: string; currency: string; price: string; qty: number;
} => ({
  isManual: form.isManual,
  label: form.label?.trim() || null,
  partNo: form.partNo?.trim() || null,
  description: form.description.trim(),
  category: form.category?.trim() || null,
  toolingType: form.toolingType?.trim() || null,
  machineType: form.machineType?.trim() || null,
  itemName: form.itemName?.trim() || null,
  partName: form.partName?.trim() || null,
  currency: form.currency,
  price: String(form.price),
  qty: form.qty,
});

export const ManualQuoteItemsPage = () => {
  const { data, isLoading, isError, error } = useManualQuoteItems();
  const allItems: ManualQuoteItem[] = useMemo(() => data?.data ?? [], [data]);

  // The endpoint returns the full list (no pagination), so label search filters
  // client-side rather than round-tripping to svc-04.
  const [search, setSearch] = useState("");
  const items = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return allItems;
    return allItems.filter((i) => (i.label ?? "").toLowerCase().includes(needle));
  }, [allItems, search]);
  const total = items.length;

  const [modal, setModal] = useState<ModalState>(null);
  const [deleteTarget, setDeleteTarget] = useState<ManualQuoteItem | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const createMutation = useCreateManualQuoteItem();
  const updateMutation = useUpdateManualQuoteItem();
  const deleteMutation = useDeleteManualQuoteItem();

  const { register, handleSubmit, reset, control, formState: { errors } } = useForm<ManualQuoteItemFormData>({ resolver: zodResolver(manualQuoteItemSchema) });

  const openAdd = () => { setFormError(null); reset(newBlank()); setModal({ mode: "add" }); };
  const openEdit = (item: ManualQuoteItem) => { setFormError(null); reset(toForm(item)); setModal({ mode: "edit", item }); };
  const openView = (item: ManualQuoteItem) => { setFormError(null); reset(toForm(item)); setModal({ mode: "view", item }); };
  const closeModal = () => { setModal(null); setFormError(null); };

  const onSubmit = (form: ManualQuoteItemFormData) => {
    setFormError(null);
    const body = toBody(form);
    if (modal?.mode === "edit" && modal.item) {
      const updateBody: UpdateManualQuoteItemBody = body;
      updateMutation.mutate(
        { id: modal.item.id, body: updateBody },
        {
          onSuccess: closeModal,
          onError: (e) => setFormError(apiErrorMessage(e, "Failed to save record.")),
        },
      );
    } else {
      // `id` is intentionally NOT sent — the server generates the UUID.
      const createBody: CreateManualQuoteItemBody = body;
      createMutation.mutate(createBody, {
        onSuccess: closeModal,
        onError: (e) => setFormError(apiErrorMessage(e, "Failed to create record.")),
      });
    }
  };

  const isView = modal?.mode === "view";
  const saving = createMutation.isPending || updateMutation.isPending;

  const cols: ColDef<ManualQuoteItem>[] = [
    { header: "id", key: "id" },
    { header: "isManual", render: (i) => String(i.isManual) },
    { header: "label", render: (i) => i.label || "—" },
    { header: "partNo", render: (i) => i.partNo || "—" },
    { header: "description", render: (i) => i.description || "—" },
    { header: "category", render: (i) => i.category || "—" },
    { header: "Actions", render: (i) => <div className="flex space-x-2"><ViewButton onClick={() => openView(i)} /><EditButton onClick={() => openEdit(i)} /><DeleteButton onClick={() => setDeleteTarget(i)} /></div> },
  ];

  const titleMap = { add: "Add Manual Quote Items record", edit: "Edit Manual Quote Items record", view: "View Manual Quote Items record" };

  return (
    <>
      <PageHeader title="Dataset: Manual Quote Items" subtitle={`Manual Quote Items records: ${total}`} action={<AddButton onClick={openAdd} />} />

      {isError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-body">
          Failed to load manual quote items{error instanceof Error ? `: ${error.message}` : "."}
        </div>
      )}

      <TableSearch
        value={search}
        onChange={setSearch}
        onClear={() => setSearch("")}
        placeholder="Search label…"
      />

      <AdminTable columns={cols} rows={items} keyExtractor={(i) => i.id} loading={isLoading} />

      <AdminModal open={modal !== null} onClose={closeModal} title={modal ? titleMap[modal.mode] : ""} maxWidth="max-w-2xl"
        footer={
          isView ? <CancelButton onClick={closeModal} /> :
          <><CancelButton onClick={closeModal} /><SaveButton onClick={handleSubmit(onSubmit)} label={saving ? "Saving…" : "Save"} /></>
        }>
        <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
          {formError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-700 text-body">
              {formError}
            </div>
          )}
          <PennField label="ID" htmlFor="id">
            <PennInput id="id" disabled {...register("id")} />
          </PennField>

          <label className="flex items-center gap-2 select-none text-blue_grey text-body">
            <PennCheckbox disabled={isView} {...register("isManual")} />
            Manual
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <PennField label="Label" htmlFor="label" error={!isView ? errors.label?.message : undefined}>
              <PennInput id="label" disabled={isView} {...register("label")} />
            </PennField>
            <PennField label="Part No" htmlFor="partNo" error={!isView ? errors.partNo?.message : undefined}>
              <PennInput id="partNo" disabled={isView} {...register("partNo")} />
            </PennField>

            <PennField label="Description" htmlFor="description" required={!isView} error={!isView ? errors.description?.message : undefined}>
              <PennInput id="description" disabled={isView} error={!isView && !!errors.description} {...register("description")} />
            </PennField>
            <PennField label="Category" htmlFor="category" error={!isView ? errors.category?.message : undefined}>
              <PennInput id="category" disabled={isView} {...register("category")} />
            </PennField>

            <PennField label="Tooling Type" htmlFor="toolingType" error={!isView ? errors.toolingType?.message : undefined}>
              <PennInput id="toolingType" disabled={isView} {...register("toolingType")} />
            </PennField>
            <PennField label="Machine Type" htmlFor="machineType" error={!isView ? errors.machineType?.message : undefined}>
              <PennInput id="machineType" disabled={isView} {...register("machineType")} />
            </PennField>

            <PennField label="Item Name" htmlFor="itemName" error={!isView ? errors.itemName?.message : undefined}>
              <PennInput id="itemName" disabled={isView} {...register("itemName")} />
            </PennField>
            <PennField label="Part Name" htmlFor="partName" error={!isView ? errors.partName?.message : undefined}>
              <PennInput id="partName" disabled={isView} {...register("partName")} />
            </PennField>

            <PennField label="Currency" htmlFor="currency" required={!isView} error={!isView ? errors.currency?.message : undefined}>
              <Controller
                name="currency"
                control={control}
                render={({ field }) => (
                  <PennDropdown
                    inputId="currency"
                    name={field.name}
                    options={CURRENCY_OPTIONS}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    isDisabled={isView}
                    error={!isView && !!errors.currency}
                  />
                )}
              />
            </PennField>
            <PennField label="Price" htmlFor="price" required={!isView} error={!isView ? errors.price?.message : undefined}>
              <PennInput id="price" type="number" step="0.01" disabled={isView} error={!isView && !!errors.price} {...register("price")} />
            </PennField>

            <PennField label="Qty" htmlFor="qty" required={!isView} error={!isView ? errors.qty?.message : undefined}>
              <PennInput id="qty" type="number" disabled={isView} error={!isView && !!errors.qty} {...register("qty")} />
            </PennField>
          </div>
        </form>
      </AdminModal>

      <DeleteConfirmModal
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (!deleteTarget) return;
          deleteMutation.mutate(deleteTarget.id, { onSettled: () => setDeleteTarget(null) });
        }}
        itemLabel={deleteTarget?.description ?? undefined}
      />
    </>
  );
};
